import { logger } from "@/utils/logger";
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { DealerManager } from '@/utils/dealerManager';

interface DealerSessionData {
  dealerId: number;
  domain: string;
  dealerName: string;
  welcomeMessage: { en: string; fr: string };
  isTradeBoostEnabled: boolean;
  sendUserMessagesTo?: string;
  vectorStoreId: string | null;
  popularQuestionsEnabled: boolean;
  tradeBannerEnabled: boolean;
  waitingVideo: boolean;
  videosEnabled: boolean;
  useMessageRouting: boolean;
  serviceFormMode?: 'Site' | 'Chat';
  paymentsEnabled: boolean;
  paymentFrequency: 'monthly' | 'biweekly' | 'weekly';
  speechToSpeech: boolean; // New: enables full speech-to-speech mode
  streamEnabled: boolean; // Enables streaming intro display (disabled by default)
}

interface DealerSessionContextType {
  sessionData: DealerSessionData | null;
  isLoading: boolean;
  error: string | null;
  refreshSession: () => Promise<void>;
  forceRefresh: () => Promise<void>;
}

const DealerSessionContext = createContext<DealerSessionContextType | undefined>(undefined);

export const DealerSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessionData, setSessionData] = useState<DealerSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializationRef = useRef(false);

  const loadSessionData = async () => {
    if (initializationRef.current) {
      logger.log('🛡️ Session initialization already in progress, skipping...');
      return;
    }
    
    initializationRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      logger.log('🔥 DEALER SESSION: Initializing session data for Responses API...');
      
      // Initialize dealer configuration
      const config = await DealerManager.initializeFromGlobalSettings();
      
      // Fetch dealer data (no Assistant ID needed for Responses API)
      const [
        dealerName,
        welcomeMessageEn,
        welcomeMessageFr
      ] = await Promise.all([
        DealerManager.getDealerName(),
        DealerManager.getWelcomeMessage('en'),
        DealerManager.getWelcomeMessage('fr')
      ]);

      const sessionData: DealerSessionData = {
        dealerId: config.dealer_id,
        domain: config.domain,
        dealerName,
        welcomeMessage: { en: welcomeMessageEn, fr: welcomeMessageFr },
        isTradeBoostEnabled: DealerManager.isTradeBoostEnabled(),
        vectorStoreId: DealerManager.getVectorStoreId(),
        popularQuestionsEnabled: config.popular_questions_enabled || false,
        tradeBannerEnabled: config.trade_banner_enabled || false,
        waitingVideo: config.waiting_video || false,
        videosEnabled: config.videos_enabled !== false,
        useMessageRouting: config.use_message_routing || false,
        serviceFormMode: config.service_form_mode,
        paymentsEnabled: config.payments || false,
        paymentFrequency: (['monthly','biweekly','weekly'] as const).includes((config as any).payment_frequency) ? (config as any).payment_frequency : 'monthly',
        speechToSpeech: config.speech_to_speech || false,
        streamEnabled: config.stream || false // Streaming disabled by default
      };

      setSessionData(sessionData);
      logger.log('✅ DEALER SESSION: Responses API session initialized successfully');
      
      // Dispatch event to notify styles and other listeners that dealer has changed
      window.dispatchEvent(new CustomEvent('dealerChanged'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dealer session';
      console.error('❌ DEALER SESSION: Failed to load session data:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async () => {
    initializationRef.current = false;
    DealerManager.clearCache();
    await loadSessionData();
  };

  const forceRefresh = async () => {
    logger.log('🔄 DEALER SESSION: Force refresh requested');
    initializationRef.current = false;
    setIsLoading(true);
    setError(null);
    setSessionData(null);
    DealerManager.forceRefresh();
    await loadSessionData();
  };

  useEffect(() => {
    loadSessionData();
  }, []);

  return (
    <DealerSessionContext.Provider value={{ sessionData, isLoading, error, refreshSession, forceRefresh }}>
      {children}
    </DealerSessionContext.Provider>
  );
};

export const useDealerSession = (): DealerSessionContextType => {
  const context = useContext(DealerSessionContext);
  if (context === undefined) {
    throw new Error('useDealerSession must be used within a DealerSessionProvider');
  }
  return context;
};